import React, { ChangeEvent, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AudioEditModal from '../components/AudioLibrary/AudioEditModal';
import ConfirmDialog from '../components/ConfirmDialog';
import DuplicatesView from '../components/AudioLibrary/DuplicatesView';
import {
  baseName, childFolders, isAudioFile, joinPath, parentOf,
} from '../components/AudioLibrary/folders';
import { formatTime } from '../components/AudioLibrary/formatTime';
import apiClient, { API_URL } from '../services/api';
import useResource from '../services/useResource';
import { AudioAsset, MaybePaginated } from '../types/api';
import './AudioLibraryPage.css';

/**
 * Reusable audio library laid out like a file browser: one stacked
 * list of folders then tracks, an "up" row to climb out, breadcrumb
 * for direct jumps. Row actions: favorite star, Edit (modal with
 * preview / markers / rename / move), Download, Delete. Directory
 * uploads keep their structure; tracks feed the post wizard's music
 * picker.
 */
export default function AudioLibraryPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dirInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDupes, setShowDupes] = useState(false);
  const [editing, setEditing] = useState<AudioAsset | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AudioAsset | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const folder = searchParams.get('folder') || '';

  const setFolder = (path: string): void => {
    setSearchParams(path ? { folder: path } : {});
  };

  const { data: allFolders, reload: reloadFolders } = useResource<string[], { folders: string[] }>(
    () => apiClient.get<{ folders: string[] }>('/audiolib/audio/folders/'),
    [],
    { initial: [], transform: (raw) => raw.folders || [] },
  );

  const { data: assets, loading, reload, setData } = useResource<AudioAsset[], MaybePaginated<AudioAsset>>(
    () => apiClient.get<MaybePaginated<AudioAsset>>(
      `/audiolib/audio/?folder=${encodeURIComponent(folder)}`,
    ),
    [folder],
    { initial: [], transform: (raw) => (Array.isArray(raw) ? raw : raw.results || []) },
  );

  const subfolders = childFolders(allFolders, folder);
  const crumbs = folder ? folder.split('/') : [];

  async function uploadBatch(items: Array<{ file: File; target: string }>): Promise<void> {
    setError(null);
    try {
      for (let i = 0; i < items.length; i += 1) {
        setUploadNote(`Uploading ${i + 1}/${items.length}…`);
        const form = new FormData();
        form.append('file', items[i].file);
        if (items[i].target) form.append('folder', items[i].target);
        await apiClient.uploadFile<AudioAsset>('/audiolib/audio/', form);
      }
      await Promise.all([reload(), reloadFolders()]);
    } catch (err) {
      setError((err as Error).message || 'Upload failed');
    } finally {
      setUploadNote(null);
    }
  }

  async function handleFiles(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!files.length) return;
    await uploadBatch(files.map((file) => ({ file, target: folder })));
  }

  async function handleDirectory(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(e.target.files || []);
    if (dirInputRef.current) dirInputRef.current.value = '';
    const audioFiles = files.filter(isAudioFile);
    if (!audioFiles.length) {
      if (files.length) setError('No audio files found in that folder.');
      return;
    }
    // webkitRelativePath is "PickedDir/Sub/track.mp3" — keep the
    // directory structure, rooted at the folder currently open.
    await uploadBatch(audioFiles.map((file) => {
      const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      const dir = parentOf(rel.replace(/\\/g, '/'));
      return { file, target: dir ? joinPath(folder, dir) : folder };
    }));
  }

  async function renameCurrentFolder(): Promise<void> {
    const next = window.prompt('New folder name', baseName(folder));
    const trimmed = (next || '').trim();
    if (!trimmed || trimmed === baseName(folder)) return;
    setError(null);
    try {
      const to = joinPath(parentOf(folder), trimmed);
      await apiClient.post('/audiolib/audio/rename_folder/', { from: folder, to });
      await reloadFolders();
      setFolder(to);
    } catch (err) {
      setError((err as Error).message || 'Rename failed');
    }
  }

  function handleUpdated(updated: AudioAsset): void {
    if (editing && editing.id === updated.id) setEditing(updated);
    if (updated.folder !== folder) {
      // Moved elsewhere — drop it from this view and refresh the tree
      // in case the move minted a brand-new folder.
      setData((prev) => prev.filter((a) => a.id !== updated.id));
      reloadFolders();
    } else {
      setData((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    }
  }

  async function performDelete(asset: AudioAsset): Promise<void> {
    setPendingDelete(null);
    try {
      await apiClient.delete(`/audiolib/audio/${asset.id}/`);
      setData((prev) => prev.filter((a) => a.id !== asset.id));
      if (editing && editing.id === asset.id) setEditing(null);
      reloadFolders();
    } catch (err) {
      setError((err as Error).message || 'Delete failed');
    }
  }

  function toggleFavorite(asset: AudioAsset): void {
    const next = !asset.is_favorite;
    setData((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: next } : a)));
    apiClient.patch<AudioAsset>(`/audiolib/audio/${asset.id}/`, { is_favorite: next })
      .catch(() => {
        setData((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: !next } : a)));
      });
  }

  const empty = !loading && assets.length === 0 && subfolders.length === 0;

  return (
    <div className="audio-library-page">
      <div className="audio-library-head">
        <h2>Audio Library</h2>
        <div className="audio-library-head-actions">
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setShowDupes((v) => {
                // Dup-view deletions happen outside this page's lists —
                // refresh them when coming back to the folder browser.
                if (v) { reload(); reloadFolders(); }
                return !v;
              });
            }}
          >
            {showDupes ? '← Back to library' : 'Show duplicates'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac,.opus"
            multiple
            onChange={handleFiles}
            hidden
          />
          <input
            ref={dirInputRef}
            type="file"
            multiple
            onChange={handleDirectory}
            hidden
            {...({ webkitdirectory: '' } as unknown as React.InputHTMLAttributes<HTMLInputElement>)}
          />
          <button
            type="button"
            className="secondary"
            onClick={() => dirInputRef.current?.click()}
            disabled={!!uploadNote}
          >
            {uploadNote || '+ Upload folder'}
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!!uploadNote}
          >
            {uploadNote ? 'Uploading…' : '+ Upload audio'}
          </button>
        </div>
      </div>
      <p className="muted">
        Uploads stay on the server so you can reuse them across posts and projects.
        Folder uploads keep their structure; new files land in the folder you have open.
        Edit a track to preview it and save start markers — the post wizard offers them
        when you pair audio with a photo.
      </p>

      {!showDupes && (
      <nav className="audio-breadcrumb" aria-label="Folder path">
        <button
          type="button"
          className={folder ? 'ghost btn--sm' : 'ghost btn--sm is-current'}
          onClick={() => setFolder('')}
        >
          Library
        </button>
        {crumbs.map((seg, i) => {
          const path = crumbs.slice(0, i + 1).join('/');
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={path}>
              <span className="audio-breadcrumb-sep" aria-hidden>/</span>
              <button
                type="button"
                className={isLast ? 'ghost btn--sm is-current' : 'ghost btn--sm'}
                onClick={() => setFolder(path)}
              >
                {seg}
              </button>
            </React.Fragment>
          );
        })}
        {folder && (
          <button
            type="button"
            className="ghost btn--sm audio-breadcrumb-rename"
            onClick={renameCurrentFolder}
            title="Rename this folder"
          >
            ✎ Rename
          </button>
        )}
      </nav>
      )}

      {error && <div className="banner banner--error">{error}</div>}

      {showDupes && <DuplicatesView onError={setError} />}

      {!showDupes && (
      <>
      {loading && <p className="muted">Loading…</p>}
      {empty && (
        <div className="panel audio-library-empty">
          <p className="muted">
            {folder
              ? 'This folder is empty. Folders disappear once their last track moves out.'
              : 'No audio yet. Upload a track or a whole folder to get started.'}
          </p>
        </div>
      )}

      {!empty && !loading && (
        <ul className="audio-fs panel">
          {folder && (
            <li>
              <button
                type="button"
                className="audio-fs-row audio-fs-nav"
                onClick={() => setFolder(parentOf(folder))}
                aria-label="Up one folder"
              >
                <span className="audio-fs-icon" aria-hidden>↑</span>
                <span className="audio-fs-name">..</span>
              </button>
            </li>
          )}
          {subfolders.map((path) => (
            <li key={path}>
              <button
                type="button"
                className="audio-fs-row audio-fs-nav"
                onClick={() => setFolder(path)}
              >
                <span className="audio-fs-icon" aria-hidden>📁</span>
                <span className="audio-fs-name">{baseName(path)}</span>
              </button>
            </li>
          ))}
          {assets.map((a) => (
            <li key={a.id} className="audio-fs-row audio-fs-file">
              <button
                type="button"
                className={a.is_favorite ? 'audio-fs-fav is-fav' : 'audio-fs-fav'}
                onClick={() => toggleFavorite(a)}
                aria-pressed={a.is_favorite}
                aria-label={a.is_favorite ? `Unfavorite ${a.name}` : `Favorite ${a.name}`}
              >
                {a.is_favorite ? '★' : '☆'}
              </button>
              <span className="audio-fs-icon" aria-hidden>♪</span>
              <span className="audio-fs-name" title={a.original_filename || a.name}>{a.name}</span>
              <span className="muted audio-fs-duration">
                {a.duration != null ? formatTime(a.duration) : '—'}
              </span>
              <span className="audio-fs-actions">
                <button
                  type="button"
                  className="secondary btn--icon"
                  onClick={() => setEditing(a)}
                  aria-label={`Edit ${a.name}`}
                  title="Edit — preview, markers, rename, move"
                >
                  ✎
                </button>
                <a
                  className="secondary btn--icon"
                  href={`${API_URL}/audiolib/audio/${a.id}/download/`}
                  download
                  aria-label={`Download ${a.name}`}
                  title="Download"
                >
                  ⤓
                </a>
                <button
                  type="button"
                  className="danger btn--icon"
                  onClick={() => setPendingDelete(a)}
                  aria-label={`Delete ${a.name}`}
                  title="Delete"
                >
                  🗑
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      </>
      )}

      {editing && (
        <AudioEditModal
          asset={editing}
          folders={allFolders}
          onClose={() => setEditing(null)}
          onUpdated={handleUpdated}
          onDelete={() => setPendingDelete(editing)}
          onError={setError}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          message={(
            <p className="muted">
              Removes it from the library{pendingDelete.folder ? ` (${pendingDelete.folder})` : ''}.
              Posts and projects that already used it keep their own copy.
            </p>
          )}
          onConfirm={() => performDelete(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
